import { Box, Button, Typography } from '@mui/material';
import AttachIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Attach.js';
import { useRef, useState } from 'react';
import { ATLAS_LENS_COLORS } from '../Form/atlasLensTokens';
import {
  lensFocusRingGutter,
  lensSpacing,
  onboardingLensLayout,
  onboardingTypography,
  useLensTheme,
} from './onboardingLens';
import { resolveAtlasLensColors } from '../Form/formFieldSpec';

interface OnboardingInlineAttachProps {
  label: string;
  onClick: () => void;
}

/** Figma Building blocks / Uploader / Inline attach — Atlas Lens secondary outlined button. */
export function OnboardingInlineAttach({ label, onClick }: OnboardingInlineAttachProps) {
  const { tokens } = useLensTheme();
  const layout = onboardingLensLayout(tokens);
  const typography = onboardingTypography(tokens);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const { outlineActive, focusRing } = resolveAtlasLensColors({ tokens });

  const attachFocusedSx = {
    outline: 'none',
    borderColor: outlineActive,
    boxShadow: focusRing,
  } as const;

  const handleClick = () => {
    onClick();
    requestAnimationFrame(() => {
      buttonRef.current?.focus();
      setIsFocused(true);
    });
  };

  return (
    <Button
      ref={buttonRef}
      variant="outlined"
      startIcon={<AttachIcon size="lg" />}
      onClick={handleClick}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      sx={{
        textTransform: 'none',
        justifyContent: 'center',
        flexShrink: 0,
        width: 'fit-content',
        height: layout.formField.inputHeight,
        borderRadius: layout.footer.skipButtonRadius,
        borderColor: ATLAS_LENS_COLORS.typeDefault,
        color: ATLAS_LENS_COLORS.typeDefault,
        ...typography.button,
        px: layout.formField.inputPaddingX,
        py: layout.formField.inputPaddingY,
        gap: layout.formField.labelToInputGap,
        outline: 'none',
        ...(isFocused ? attachFocusedSx : {}),
        '&:hover': {
          borderColor: isFocused ? outlineActive : ATLAS_LENS_COLORS.typeDefault,
          backgroundColor: 'transparent',
        },
        '&&:focus': attachFocusedSx,
        '&&.Mui-focusVisible': attachFocusedSx,
        '& .MuiButton-startIcon': {
          margin: 0,
        },
      }}
    >
      {label}
    </Button>
  );
}

interface OnboardingAttachFormatHintsProps {
  formats: string;
  maxSize: string;
}

/** Figma Details row — format hints with Lens spacing separator dot. */
export function OnboardingAttachFormatHints({ formats, maxSize }: OnboardingAttachFormatHintsProps) {
  const { tokens } = useLensTheme();
  const typography = onboardingTypography(tokens);
  const hintSx = {
    ...typography.labelRequired,
    color: ATLAS_LENS_COLORS.typeMuted,
    whiteSpace: 'nowrap' as const,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: lensSpacing(tokens, '0_5'),
        flexShrink: 1,
        minWidth: 0,
      }}
    >
      <Typography sx={hintSx}>{formats}</Typography>
      <Box
        sx={{
          width: lensSpacing(tokens, '0_5'),
          height: lensSpacing(tokens, '0_5'),
          borderRadius: '50%',
          bgcolor: ATLAS_LENS_COLORS.typeMuted,
          flexShrink: 0,
        }}
      />
      <Typography sx={hintSx}>{maxSize}</Typography>
    </Box>
  );
}

/** Figma inline attach row — button + format hints. */
export function OnboardingInlineAttachRow({
  label,
  formats,
  maxSize,
  onClick,
}: OnboardingInlineAttachProps & OnboardingAttachFormatHintsProps) {
  const { tokens } = useLensTheme();
  const layout = onboardingLensLayout(tokens);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: layout.formSection.sectionGap,
        flexWrap: 'nowrap',
        width: 'fit-content',
        maxWidth: '100%',
        overflow: 'visible',
        p: lensFocusRingGutter(tokens),
      }}
    >
      <OnboardingInlineAttach label={label} onClick={onClick} />
      <OnboardingAttachFormatHints formats={formats} maxSize={maxSize} />
    </Box>
  );
}
